/**
 * WorldBridge.ts
 * postMessage bridge between Dungeon Master (iframe) and poqpoq World (parent).
 *
 * When DM runs embedded in World, this module handles:
 *   - Sending dungeon data to World on save ("dungeon_saved")
 *   - Requesting modal close ("tool_close")
 *   - Receiving context from World ("init-context", "load_dungeon")
 *
 * Pattern matches Terraformer/Landscaper bridge in World's MyWorldsPanel.
 */

import type { AppContext } from '../editor/ModeDetector.ts'
import type { OctileDungeon } from '../types/OctileTypes.ts'
import { serializeBBD } from './DungeonIO.ts'

// ─── Message Types ─────────────────────────────────────────

/** Messages DM sends to World */
export interface DMToWorldMessage {
    type: 'dungeon_saved' | 'tool_close'
    source: 'dungeonmaster'
    instanceId?: string | null
    payload?: {
        dungeonData: string   // serialized BBD JSON
        timestamp: string     // ISO8601
    }
}

/** Messages World sends to DM */
export interface WorldToDMMessage {
    type: 'load_dungeon' | 'AUTH_TOKEN' | 'init-context'
    source: 'blackbox-world'
    payload?: unknown
}

// ─── Callbacks ─────────────────────────────────────────────

export interface WorldBridgeCallbacks {
    /** Called when World sends a dungeon to load (editing existing) */
    onLoadDungeon?: (bbdJson: string) => void
    /** Called when World sends auth context */
    onAuthReceived?: (token: string, userId: string) => void
}

// ─── WorldBridge ───────────────────────────────────────────

export class WorldBridge {
    private context: AppContext
    private callbacks: WorldBridgeCallbacks
    private listening = false

    constructor(context: AppContext, callbacks: WorldBridgeCallbacks = {}) {
        this.context = context
        this.callbacks = callbacks

        if (this.isEmbedded()) {
            this.startListening()
        }
    }

    /** True when running inside World's iframe with an instance ID */
    isEmbedded(): boolean {
        return this.context.mode === 'embedded'
    }

    /**
     * Send the current dungeon to World for NEXUS persistence.
     * World will persist it, create a portal, and optionally close the modal.
     */
    saveDungeon(dungeon: OctileDungeon): void {
        if (!this.isEmbedded()) {
            console.warn('[WorldBridge] saveDungeon called in standalone mode — ignored')
            return
        }

        const message: DMToWorldMessage = {
            type: 'dungeon_saved',
            source: 'dungeonmaster',
            instanceId: this.context.instanceId,
            payload: {
                dungeonData: serializeBBD(dungeon),
                timestamp: new Date().toISOString(),
            },
        }

        window.parent.postMessage(message, '*')
        console.log(`[WorldBridge] dungeon_saved → World (instance: ${this.context.instanceId})`)
    }

    /**
     * Request World to close the DM modal iframe.
     */
    requestClose(): void {
        if (!this.isEmbedded()) return

        const message: DMToWorldMessage = {
            type: 'tool_close',
            source: 'dungeonmaster',
        }

        window.parent.postMessage(message, '*')
        console.log('[WorldBridge] tool_close → World')
    }

    /**
     * Save and close in one action (typical "Save to World" button flow).
     */
    saveAndClose(dungeon: OctileDungeon): void {
        this.saveDungeon(dungeon)
        // Small delay so World can process the save before the modal closes
        setTimeout(() => this.requestClose(), 200)
    }

    /** Clean up event listener */
    dispose(): void {
        if (this.listening) {
            window.removeEventListener('message', this.handleMessage)
            this.listening = false
        }
    }

    // ─── Private ───────────────────────────────────────

    private startListening(): void {
        window.addEventListener('message', this.handleMessage)
        this.listening = true
        console.log('[WorldBridge] Listening for World messages')
    }

    private handleMessage = (event: MessageEvent): void => {
        const data = event.data
        if (!data || typeof data !== 'object') return

        // Only handle messages from World
        if (data.source !== 'blackbox-world' && data.type !== 'load_dungeon' && data.type !== 'AUTH_TOKEN') return

        switch (data.type) {
            case 'load_dungeon':
                if (data.payload && typeof data.payload === 'string') {
                    console.log('[WorldBridge] Received dungeon to load from World')
                    this.callbacks.onLoadDungeon?.(data.payload)
                }
                break

            case 'AUTH_TOKEN':
                if (data.payload?.token && data.payload?.userId) {
                    console.log('[WorldBridge] Received auth from World')
                    this.callbacks.onAuthReceived?.(data.payload.token, data.payload.userId)
                }
                break

            case 'init-context':
                console.log('[WorldBridge] Received init context from World')
                // Future: handle additional context (world metadata, existing dungeon ID, etc.)
                break
        }
    }
}
